import React, { createContext, useContext, useState, useEffect, ReactNode } from 'react';

interface User {
  id: number;
  googleId: string;
  email: string;
  name: string;
  pictureUrl?: string;
  role: 'admin' | 'manager' | 'user';
  isActive: boolean;
  lastLoginAt?: string;
}

interface AuthContextType {
  user: User | null;
  loading: boolean;
  login: (email: string, password: string) => Promise<void>;
  logout: () => void;
  isAuthenticated: boolean;
}

const AuthContext = createContext<AuthContextType | undefined>(undefined);

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (context === undefined) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};

interface AuthProviderProps {
  children: ReactNode;
}

export const AuthProvider: React.FC<AuthProviderProps> = ({ children }) => {
  const [user, setUser] = useState<User | null>(null);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    // 檢查本地儲存的會話
    const checkSession = async () => {
      try {
        const token = localStorage.getItem('sessionToken');
        if (token) {
          // 暫時模擬用戶資料
          setUser({
            id: 1,
            googleId: 'demo',
            email: 'demo@example.com',
            name: 'Demo User',
            role: 'admin',
            isActive: true
          });
        }
      } catch (error) {
        console.error('會話檢查失敗:', error);
        localStorage.removeItem('sessionToken');
      } finally {
        setLoading(false);
      }
    };

    checkSession();
  }, []);

  const login = async (email: string, password: string) => {
    try {
      setLoading(true);
      // 暫時模擬登入
      const mockUser: User = {
        id: 1,
        googleId: 'demo',
        email: email,
        name: 'Demo User',
        role: 'admin',
        isActive: true
      };
      
      localStorage.setItem('sessionToken', 'demo-token');
      setUser(mockUser);
    } catch (error) {
      console.error('登入失敗:', error);
      throw error;
    } finally {
      setLoading(false);
    }
  };

  const logout = () => {
    localStorage.removeItem('sessionToken');
    setUser(null);
  };

  const value: AuthContextType = {
    user,
    loading,
    login,
    logout,
    isAuthenticated: !!user,
  };

  return (
    <AuthContext.Provider value={value}>
      {children}
    </AuthContext.Provider>
  );
};